/**
 * When an Admin 401 may refresh the session, and what a refresh failure
 * means. Kept free of ky, the store and window so the rules are unit
 * tested directly; api.ts wires the real dependencies in.
 *
 * The rules (22 October 2026 early sign-out fixes):
 *   - A 401 from a sign-in endpoint is wrong input (bad password, used
 *     link, wrong code), not an expired token, so it never refreshes.
 *     Neither does a tab with no session.
 *   - Every waiting 401 shares ONE refresh call.
 *   - Only a 401 or 403 from /auth/refresh ends the session. A network
 *     error, timeout, 429 or 5xx is retried once after 500 ms, and if it
 *     still fails the caller gets its original 401 with the session kept.
 *   - A refreshed token for a different user than the stored one (the
 *     cookie is per browser, not per tab) ends this tab's session rather
 *     than mixing identities.
 */

/** Sign-in and pre-session endpoints. auth/me and auth/mfa/admin/reset/:id
 *  are calls made inside a session and keep refreshing. */
const PRE_SESSION_PATH =
  /\/auth\/(login|logout|refresh|magic-link(\/verify)?|mfa\/(enroll\/(start|verify)|challenge)|forgot-password|reset-password)$/;
const SALES_PARTNER_INVITE_PATH = /\/sales-partner-invites(\/|$)/;

/** True for a request URL (absolute or relative, query allowed) whose 401
 *  must not trigger a refresh. */
export function isPreSessionAuthPath(url: string): boolean {
  let pathname: string;
  try {
    pathname = new URL(url, 'http://localhost').pathname;
  } catch {
    pathname = url.split('?')[0] ?? url;
  }
  pathname = pathname.replace(/\/+$/, '');
  return PRE_SESSION_PATH.test(pathname) || SALES_PARTNER_INVITE_PATH.test(pathname);
}

export function shouldAttemptRefresh(url: string, isAuthenticated: boolean): boolean {
  return isAuthenticated && !isPreSessionAuthPath(url);
}

export type RefreshOutcome =
  | { kind: 'success'; token: string }
  /** The server refused the refresh: the session is over. */
  | { kind: 'refused'; status: number }
  /** Network error, timeout, 429, 5xx or an unreadable body: try again later. */
  | { kind: 'transient' };

/** Sort an error thrown by the refresh POST. ky's HTTPError carries the
 *  response; TimeoutError, a fetch TypeError and anything else do not. */
export function classifyRefreshError(err: unknown): RefreshOutcome {
  const status = (err as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  if (status === 401 || status === 403) return { kind: 'refused', status };
  return { kind: 'transient' };
}

/** Sort a 2xx refresh body. A 2xx without a token is not the server
 *  refusing (refusals are 401s), so it does not end the session. */
export function outcomeFromRefreshBody(body: unknown): RefreshOutcome {
  const b = body as { success?: unknown; data?: { access_token?: unknown } | null } | null | undefined;
  const token = b?.success === true ? b.data?.access_token : undefined;
  if (typeof token === 'string' && token.length > 0) return { kind: 'success', token };
  return { kind: 'transient' };
}

export const REFRESH_RETRY_DELAY_MS = 500;

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** One attempt, and one more after a pause if the first was transient. */
export async function refreshWithOneRetry(
  attempt: () => Promise<RefreshOutcome>,
  sleep: (ms: number) => Promise<void> = defaultSleep,
  delayMs: number = REFRESH_RETRY_DELAY_MS,
): Promise<RefreshOutcome> {
  const first = await attempt();
  if (first.kind !== 'transient') return first;
  await sleep(delayMs);
  return attempt();
}

/** Callers that arrive while `run` is in flight share its promise. */
export function shareInFlight<T>(run: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;
  return () => {
    if (!inFlight) {
      inFlight = run().finally(() => { inFlight = null; });
    }
    return inFlight;
  };
}

/** The `sub` claim of a JWT, read without verification (the Api verifies
 *  every request). Null when the token is not a well-formed JWT. Ported
 *  from apps/web/src/lib/jwt.ts. */
export function decodeJwtSub(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadSegment = parts[1];
    if (!payloadSegment) return null;
    const b64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json: unknown = JSON.parse(atob(padded));
    if (json && typeof json === 'object' && 'sub' in json) {
      const sub = (json as { sub: unknown }).sub;
      return typeof sub === 'string' ? sub : null;
    }
    return null;
  } catch {
    return null;
  }
}

/** Only a KNOWN different user counts; a missing sub or stored id does
 *  not block a refresh. */
export function tokenBelongsToOtherUser(token: string, storedUserId: string | null | undefined): boolean {
  const sub = decodeJwtSub(token);
  return Boolean(sub && storedUserId && sub !== storedUserId);
}

/** Where an ended session lands. The cause rides the page-load URL so the
 *  sign-out can be told apart afterwards. */
export function sessionEndedLoginPath(cause: string): string {
  return `/login?reason=expired&cause=${encodeURIComponent(cause)}`;
}

export function refusalCause(status: number): string {
  return `refresh_${status}`;
}

/**
 * What a replay keeps from the request it repeats (29 Oct 2026). Read from
 * the options ky hands the afterResponse hook, which carry the instance's
 * and the call's settings at run time (ky's NormalizedOptions type does not
 * list timeout, but the object holds it): the call's own timeout (a
 * download's 2 minutes, the client's 30 s), its retry policy (gateway codes,
 * GET only) and its fetch. Without them `ky(request)` replays with ky's
 * defaults, a 10 s timeout and two retries on any 5xx. Hooks, headers and
 * the signal are never copied, so a replay cannot refresh again.
 */
export interface ReplayOptions {
  timeout?: number | false;
  retry?: unknown;
  fetch?: unknown;
}

export function replayOptionsFrom(options: unknown): ReplayOptions {
  if (!options || typeof options !== 'object') return {};
  const o = options as { timeout?: unknown; retry?: unknown; fetch?: unknown };
  const out: ReplayOptions = {};
  if (typeof o.timeout === 'number' || o.timeout === false) out.timeout = o.timeout;
  if (o.retry !== undefined) out.retry = o.retry;
  if (typeof o.fetch === 'function') out.fetch = o.fetch;
  return out;
}

export interface SessionRefreshDeps {
  /** One POST /auth/refresh, already sorted. Must not throw. */
  attempt: () => Promise<RefreshOutcome>;
  getAuth: () => { isAuthenticated: boolean; accessToken: string | null; userId: string | null | undefined };
  setAccessToken: (token: string) => void;
  /** Clear auth and send the tab to sign-in with this cause. */
  endSession: (cause: string) => void;
  /** Re-send a request outside the hook (no second refresh), with the
   *  original call's timeout, retry and fetch (replayOptionsFrom). */
  replay: (request: Request, options: ReplayOptions) => Promise<Response>;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * The afterResponse handler for the `api` instance. The shared refresh
 * applies its store effects once, inside the in-flight promise, so eight
 * parallel 401s give one POST, one token write and at most one sign-out.
 */
export function createSessionRefresher(deps: SessionRefreshDeps) {
  const refreshOnce = shareInFlight(async (): Promise<string | null> => {
    const outcome = await refreshWithOneRetry(deps.attempt, deps.sleep);
    if (outcome.kind === 'success') {
      if (tokenBelongsToOtherUser(outcome.token, deps.getAuth().userId)) {
        deps.endSession('sub_mismatch');
        return null;
      }
      deps.setAccessToken(outcome.token);
      return outcome.token;
    }
    if (outcome.kind === 'refused') deps.endSession(refusalCause(outcome.status));
    return null;
  });

  /** `options` is what ky passes the hook as its second argument; the
   *  replay keeps that call's timeout, retry and fetch. */
  async function afterResponse(request: Request, response: Response, options?: unknown): Promise<Response | void> {
    if (response.status !== 401) return;
    const { isAuthenticated, accessToken } = deps.getAuth();
    if (!shouldAttemptRefresh(request.url, isAuthenticated)) return;

    // Sent with an older token than the store now holds: a refresh already
    // landed while this request was out, so replay instead of refreshing.
    if (accessToken && request.headers.get('Authorization') !== `Bearer ${accessToken}`) {
      request.headers.set('Authorization', `Bearer ${accessToken}`);
      return deps.replay(request, replayOptionsFrom(options));
    }

    const token = await refreshOnce();
    if (!token) return;
    request.headers.set('Authorization', `Bearer ${token}`);
    return deps.replay(request, replayOptionsFrom(options));
  }

  return { afterResponse };
}
